import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Avatar,
    Container,
    Box,
    Dialog,
    DialogContent,
    useMediaQuery,
    DialogTitle,
    IconButton,
    Typography,
    Button,
    TextField,
    MenuItem,
    Fab,
    Stack,
    Alert,
    DialogActions,
    Portal,
    Grow,
    Fade,
    Divider,
    ToggleButtonGroup,
    ToggleButton,
    Slide,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';
import { useMutation, useQueryClient, useQuery, useQueries } from '@tanstack/react-query';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { EventClickArg, EventDropArg, DatesSetArg } from '@fullcalendar/core';
import { DateClickArg, EventResizeDoneArg } from '@fullcalendar/interaction';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Appointment, AppointmentStatus, Service, BookingConfirmation, createEmptyAppointment, Employee, isBlocker, appointmentKey } from '../../types';
import { TIME_INTERVAL, SELECTION_TIME_INTERVAL } from '../../constants';
import {
    createBlocker,
    deleteBlocker,
    updateAppointment,
    updateAppointmentInEmployeeProfile,
    fetchServices,
    getCurrentBusinessConfig,
    fetchEmployeeSchedule,
    getAppointmentsByIds,
    getImageFromStorage,
} from '../../firebase/firebase-delegate';
import { dayjs, Dayjs } from '../../utils/dayjs-config';
import { extractWorkHours } from '../../time/timeUtils';
import AppointmentCard from '../../components/employee/components/MyAppointments/AppointmentCard';
import BlockerCard from '../../components/employee/components/MyAppointments/BlockerCard';
import { useScheduleHubContext } from './ScheduleHubViewModel';
import BookNowDialog from '../../components/booking/BookNowDialog';
import BookingConfirmationDetails from '../../components/booking/BookingConfirmationDetails';
import BitstringDebugDialog from '../../components/debug/BitstringDebugDialog';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import { useResultSnackbar } from '../../components/global/SuccessOrErrorSnackbar';
import { useTenant } from '../../tenancy/TenantContext';
import {
    applyAppointmentChange,
    appointmentsByEmployeeKey,
    employeeScheduleKey,
    ChangeMode,
    type EmployeeScheduleQueryKey,
    type AppointmentsByEmployeeQueryKey,
} from '../../utils/cache-delegate';
import { getCachedAssetUrl } from '../../utils/assetCache';
import { useAdminData } from '../../contexts/AdminDataContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import EmployeePicker from '../../components/admin/EmployeePicker';
import { CalendarHeaderCard, CalendarNavigationBar, WorkHoursSummaryBar } from '../../components/admin/CalendarHeaderCard';
import { resolveAdminBookingUxConfig } from '../../utils/bookingUxConfig';
import { EmployeeSchedulePanel } from './ManageSchedule/ManageSchedule';

type BlockerFormState = {
    title: string;
    start: Dayjs;
    end: Dayjs;
};

const ManageScheduleTransition = React.forwardRef(function ManageScheduleTransition(
    props: TransitionProps & { children: React.ReactElement<unknown> },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export type CalendarViewProps = {
    employee: Employee;
    appointments: Appointment[];
    appointmentIds: string[];
    schedule: Map<string, string> | null;
    sharedDate?: Dayjs;
    onSharedDateChange?: (next: Dayjs) => void;
    onlyDayView?: boolean;
    readOnly?: boolean;
    hideToolbar?: boolean;
    hideCreateButton?: boolean;
    hideDialogs?: boolean;
    embedded?: boolean;
    onAppointmentClick?: (appointment: Appointment) => void;
    showSuccess?: (msg: string) => void;
    showError?: (msg: string) => void;
    createEmployeeOptions?: Employee[];
    selectedCreateEmployeeId?: string;
    onSelectCreateEmployee?: (employeeId: string) => void;
    internalBookingEmployeesOverride?: Employee[];
    internalBookingAllowedEmployeeIds?: string[];
    internalBookingPreferredEmployeeId?: string;
    updateCurrentEmployeeIndexCache?: boolean;
    onCalendarTitleClick?: (anchorEl: HTMLElement) => void;
    workHoursLabel?: string;
    workHoursBreakLabel?: string | null;
    onWorkHoursClick?: () => void;
    stickyHeaderTop?: number | Record<string, number>;
    stickyHeaderControls?: React.ReactNode;
};

export const CalendarView: React.FC<CalendarViewProps> = ({
    employee,
    appointments,
    appointmentIds,
    schedule,
    sharedDate,
    onSharedDateChange,
    onlyDayView = false,
    readOnly = false,
    hideToolbar = false,
    hideCreateButton = false,
    hideDialogs = false,
    embedded = false,
    onAppointmentClick,
    showSuccess: showSuccessProp,
    showError: showErrorProp,
    createEmployeeOptions,
    selectedCreateEmployeeId,
    onSelectCreateEmployee,
    internalBookingEmployeesOverride,
    internalBookingAllowedEmployeeIds,
    internalBookingPreferredEmployeeId,
    updateCurrentEmployeeIndexCache = true,
    onCalendarTitleClick,
    workHoursLabel,
    workHoursBreakLabel,
    onWorkHoursClick,
    stickyHeaderTop,
    stickyHeaderControls,
}) => {
    const { tenantId } = useTenant();
    const theme = useTheme();
    const queryClient = useQueryClient();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const activeSharedDate = sharedDate ?? dayjs.tz();
    const handleSharedDateChange = onSharedDateChange ?? (() => { });
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<Appointment | null>(null);
    const [calendarTitle, setCalendarTitle] = useState('');
    const calendarRef = useRef<FullCalendar | null>(null);
    const calendarContainerRef = useRef<HTMLDivElement | null>(null);
    const [viewStart, setViewStart] = useState<Dayjs | null>(null);
    const [viewEnd, setViewEnd] = useState<Dayjs | null>(null);
    const [currentViewType, setCurrentViewType] = useState<string>(
        onlyDayView || isMobile ? 'timeGridDay' : 'timeGridWeek'
    );
    const [slotMinTime, setSlotMinTime] = useState<string>('08:00:00');
    const [slotMaxTime, setSlotMaxTime] = useState<string>('18:00:00');
    const [bitstringDialogState, setBitstringDialogState] = useState<{ dateLabel: string; bitstring: string | null; dateKey: string } | null>(null);
    const BLOCKER_DEFAULT_TITLE = 'Unavailable';
    const snapToInterval = (value: Dayjs) => {
        const snappedMinutes = Math.floor(value.minute() / SELECTION_TIME_INTERVAL) * SELECTION_TIME_INTERVAL;
        return value.clone().second(0).millisecond(0).minute(snappedMinutes);
    };
    const formatInputValue = (value: Dayjs) => value.format('YYYY-MM-DDTHH:mm');
    const [addBlockerOpen, setAddBlockerOpen] = useState(false);
    const [blockerForm, setBlockerForm] = useState<BlockerFormState>(() => {
        const start = snapToInterval(dayjs());
        return { title: BLOCKER_DEFAULT_TITLE, start, end: start.add(30, 'minute') };
    });
    const [blockerDetailsOpen, setBlockerDetailsOpen] = useState(false);
    const [selectedBlocker, setSelectedBlocker] = useState<Appointment | null>(null);
    const [blockerError, setBlockerError] = useState<string | null>(null);
    const [blockerDeleteError, setBlockerDeleteError] = useState<string | null>(null);
    const [blockerConfirmation, setBlockerConfirmation] = useState<{ title: string; message: string } | null>(null);
    const [moveConfirmation, setMoveConfirmation] = useState<{ original: Appointment; next: Appointment; action: 'move' | 'resize' } | null>(null);
    const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
    const [moveError, setMoveError] = useState<string | null>(null);
    const [handoffSuccessDialogOpen, setHandoffSuccessDialogOpen] = useState(false);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [createAnchor, setCreateAnchor] = useState<Dayjs | null>(null);
    const [createDialogMode, setCreateDialogMode] = useState<'full' | 'appointmentOnly'>('full');
    const [addAppointmentOpen, setAddAppointmentOpen] = useState(false);
    const [appointmentDraft, setAppointmentDraft] = useState<Appointment | null>(null);
    const [internalBookingConfirmation, setInternalBookingConfirmation] = useState<BookingConfirmation | null>(null);
    const showSuccess = showSuccessProp ?? (() => { });
    const showError = showErrorProp ?? (() => { });
    // Queue cache updates so we can defer refreshes until dialogs close.
    const [pendingCalendarCache, setPendingCalendarCache] = useState<Array<{
        mode: CalendarCacheMode;
        next: Appointment;
        prev?: Appointment;
        options?: { serverUpdatedSchedule?: boolean };
        waitFor: 'addAppointment' | 'move' | 'addBlocker' | 'blockerDetails' | 'blockerConfirmation';
    }>>([]);
    const pendingCalendarCacheRef = useRef<typeof pendingCalendarCache>([]);
    const syncCalendarCachesRef = useRef<null | ((
        next: Appointment,
        mode: CalendarCacheMode,
        prev?: Appointment,
        options?: { serverUpdatedSchedule?: boolean }
    ) => void)>(null);

    const formatTitle = (start: Dayjs, end: Dayjs, viewType: string): string => {
        if (viewType === 'timeGridDay') {
            return start.format('MMMM D, YYYY');
        }

        if (viewType === 'dayGridMonth') {
            return start.format('MMMM YYYY');
        }

        const displayEnd = end.subtract(1, 'day'); // FullCalendar end is exclusive
        if (start.year() === displayEnd.year()) {
            return `${start.format('MMM D')} – ${displayEnd.format('MMM D, YYYY')}`;
        }
        return `${start.format('MMM D, YYYY')} – ${displayEnd.format('MMM D, YYYY')}`;
    };

    const resolveAnchor = (startOverride?: Dayjs) => {
        if (startOverride) return snapToInterval(startOverride);
        const now = dayjs();
        const base = activeSharedDate ? activeSharedDate.startOf('day') : now;
        const merged = base.hour(now.hour()).minute(now.minute());
        return snapToInterval(merged);
    };
    const myAppointments = appointments;
    const employeeId = employee.id;
    const businessConfigQuery = useQuery({
        queryKey: ['businessConfig', tenantId],
        queryFn: () => getCurrentBusinessConfig(tenantId),
    });
    const calendarBookingUxConfig = resolveAdminBookingUxConfig(businessConfigQuery.data ?? null);
    const calendarResourceSingular = calendarBookingUxConfig.terminology.resourceSingular;
    const enabledEmployeeSMS = businessConfigQuery.data?.features.enableEmployeeSMS ?? false;
    const appointmentsQueryKey = useMemo<AppointmentsByEmployeeQueryKey | null>(
        () => (employeeId ? appointmentsByEmployeeKey(tenantId, employeeId, appointmentIds) : null),
        [tenantId, employeeId, appointmentIds]
    );
    const scheduleQueryKey = useMemo<EmployeeScheduleQueryKey | null>(
        () => (employeeId ? employeeScheduleKey(tenantId, employeeId) : null),
        [tenantId, employeeId]
    );

    const servicesQuery = useQuery<Service[]>({
        queryKey: ['services', tenantId],
        queryFn: () => fetchServices(tenantId),
        enabled: !readOnly,
    });


    const isError =
        servicesQuery.isError;

    const openBlockerModal = (startOverride?: Dayjs) => {
        if (readOnly) return;
        const snappedStart = resolveAnchor(startOverride);
        setBlockerForm({
            title: BLOCKER_DEFAULT_TITLE,
            start: snappedStart,
            end: snappedStart.add(30, 'minute'),
        });
        setBlockerError(null);
        setAddBlockerOpen(true);
    };

    const openCreationDialog = (startOverride?: Dayjs | null, autoSelect?: 'appointment' | 'blocker') => {
        if (readOnly) return;
        const anchor = startOverride ? resolveAnchor(startOverride) : null;
        const now = dayjs();
        const mode = anchor && anchor.isBefore(now) ? 'appointmentOnly' : 'full';
        if (autoSelect === 'appointment') {
            openInternalAppointmentModal(anchor);
            return;
        }
        if (autoSelect === 'blocker') {
            openBlockerModal(anchor ?? undefined);
            return;
        }
        setCreateDialogMode(mode);
        setCreateAnchor(anchor);
        setCreateDialogOpen(true);
    };

    const handleCloseCreateDialog = () => {
        setCreateDialogOpen(false);
        setCreateAnchor(null);
    };

    const openInternalAppointmentModal = (startOverride?: Dayjs | null) => {
        if (readOnly || !employee) {
            return;
        }
        const anchor = startOverride ? resolveAnchor(startOverride) : null;
        setAppointmentDraft(createEmptyAppointment({
            employeeID: employee.id,
            ...(anchor
                ? {
                    appointmentDate: anchor.format('MMDDYYYY'),
                    appointmentTime: anchor.format('HHmm'),
                }
                : {}),
        }));
        setAddAppointmentOpen(true);
    };

    const handleSelectCreateBlocker = () => {
        openBlockerModal(createAnchor ?? undefined);
        handleCloseCreateDialog();
    };

    const handleSelectCreateAppointment = () => {
        openInternalAppointmentModal(createAnchor);
        handleCloseCreateDialog();
    };
    const handleSelectEditWorkHours = () => {
        onWorkHoursClick?.();
        handleCloseCreateDialog();
    };

    const handleCloseAppointmentModal = () => {
        setAddAppointmentOpen(false);
    };

    const handleInternalAppointmentChange = (next: Appointment | null) => {
        setAppointmentDraft(next);
    };

    const handleInternalBookingSuccess = (appointment: BookingConfirmation) => {
        const sync = syncCalendarCachesRef.current;
        if (sync) {
            sync(appointment, ChangeMode.ADD);
        } else {
            setPendingCalendarCache((prev) => ([
                ...prev,
                {
                    mode: ChangeMode.ADD,
                    next: appointment,
                    waitFor: 'addAppointment',
                },
            ]));
        }
        setInternalBookingConfirmation(appointment);
    };
    type CalendarCacheMode = ChangeMode;

    /**
     * Sync all React Query caches after an appointment change.
     *
     * CalendarScreen should NOT:
     * - touch employee IDs
     * - touch schedule bitstrings
     * - touch employee.appointments maps
     *
     * It just declares WHAT changed.
     */
    const syncCalendarCaches = (
        next: Appointment,
        mode: CalendarCacheMode,
        prev?: Appointment,
        options?: { serverUpdatedSchedule?: boolean }
    ) => {
        const addIdToDate = (appointmentsMap: Record<string, string[]>, dateKey: string, appointmentId: string) => {
            const existing = appointmentsMap[dateKey] ?? [];
            if (existing.includes(appointmentId)) return appointmentsMap;
            return { ...appointmentsMap, [dateKey]: [...existing, appointmentId] };
        };
        const removeIdFromDate = (appointmentsMap: Record<string, string[]>, dateKey: string, appointmentId: string) => {
            const existing = appointmentsMap[dateKey] ?? [];
            const remaining = existing.filter((id) => id !== appointmentId);
            if (remaining.length === 0) {
                const { [dateKey]: _removed, ...rest } = appointmentsMap;
                return rest;
            }
            return { ...appointmentsMap, [dateKey]: remaining };
        };
        const patchEmployeeAppointmentIndex = (
            appointmentsMap: Record<string, string[]>,
            patchMode: CalendarCacheMode,
            patchNext: Appointment,
            patchPrev?: Appointment
        ) => {
            if (patchMode === ChangeMode.ADD) {
                return addIdToDate(appointmentsMap, patchNext.appointmentDate, patchNext.id);
            }
            if (patchMode === ChangeMode.REMOVE) {
                return removeIdFromDate(appointmentsMap, patchNext.appointmentDate, patchNext.id);
            }
            if (patchMode === ChangeMode.REPLACE) {
                let updated = appointmentsMap;
                if (patchPrev) {
                    updated = removeIdFromDate(updated, patchPrev.appointmentDate, patchPrev.id);
                }
                return addIdToDate(updated, patchNext.appointmentDate, patchNext.id);
            }
            return appointmentsMap;
        };
        const patchListByMode = (
            existing: Appointment[] | undefined,
            patchMode: CalendarCacheMode,
            patchNext: Appointment,
            patchPrev?: Appointment
        ): Appointment[] | undefined => {
            if (!existing) return existing;

            if (patchMode === ChangeMode.REMOVE) {
                return existing.filter((appointment) => appointment.id !== patchNext.id);
            }

            if (patchMode === ChangeMode.ADD) {
                const alreadyExists = existing.some((appointment) => appointment.id === patchNext.id);
                return alreadyExists
                    ? existing.map((appointment) => (appointment.id === patchNext.id ? patchNext : appointment))
                    : [...existing, patchNext];
            }

            if (patchMode === ChangeMode.REPLACE) {
                const nextIdExists = existing.some((appointment) => appointment.id === patchNext.id);
                if (nextIdExists) {
                    return existing.map((appointment) =>
                        appointment.id === patchNext.id ? patchNext : appointment
                    );
                }

                if (patchPrev) {
                    const removedPrev = existing.filter((appointment) => appointment.id !== patchPrev.id);
                    return [...removedPrev, patchNext];
                }

                return [...existing, patchNext];
            }

            return existing;
        };

        applyAppointmentChange(
            queryClient,
            mode,
            next,
            prev,
            {
                siteId: tenantId,
                // Only write to this view's employee-scoped list cache when the change targets
                // the same employee; otherwise we'd patch the wrong employee list.
                appointmentsQueryKey:
                    next.employeeID === employeeId
                        ? (appointmentsQueryKey ?? undefined)
                        : undefined,
                // Always target the actual employee schedule that changed.
                scheduleKey: next.employeeID
                    ? employeeScheduleKey(tenantId, next.employeeID)
                    : (scheduleQueryKey ?? undefined),
                // Prevent double-applying bitstring math if Firestore already did it
                serverAlreadyUpdatedScheduleBitstrings: options?.serverUpdatedSchedule ?? true,
                // currentEmployee cache should be patched when this change belongs to that employee.
                updateCurrentEmployeeIndex:
                    updateCurrentEmployeeIndexCache && next.employeeID === employeeId,
            }
        );

        // For cross-employee moves away from the current employee, remove-only from currentEmployee.appointments.
        // applyAppointmentChange(REPLACE) removes prev then adds next, which is not what we want for move-away cases.
        if (
            mode === ChangeMode.REPLACE &&
            updateCurrentEmployeeIndexCache &&
            prev &&
            prev.employeeID === employeeId &&
            next.employeeID !== employeeId
        ) {
            queryClient.setQueryData<Employee | undefined>(['currentEmployee', tenantId], (existing) => {
                if (!existing) return existing;
                const nextAppointments = removeIdFromDate(
                    { ...(existing.appointments ?? {}) },
                    prev.appointmentDate,
                    prev.id
                );
                return { ...existing, appointments: nextAppointments };
            });
        }

        // For cross-employee moves, also refresh the previous employee's schedule cache reference.
        // Firestore already applied the bitstring mutation; cloning triggers subscribers to recompute.
        if (
            mode === ChangeMode.REPLACE &&
            prev &&
            prev.employeeID !== next.employeeID &&
            prev.employeeID
        ) {
            queryClient.setQueryData<Map<string, string> | undefined>(
                employeeScheduleKey(tenantId, prev.employeeID),
                (existing) => (existing ? new Map(existing) : existing)
            );
        }

        // Keep any existing appointmentsByEmployee caches in sync for affected employees.
        const affectedEmployeeIds = new Set<string>(
            [next.employeeID, prev?.employeeID].filter(Boolean) as string[]
        );
        affectedEmployeeIds.forEach((affectedEmployeeId) => {
            queryClient.setQueriesData<Appointment[]>(
                { queryKey: ['appointmentsByEmployee', tenantId, affectedEmployeeId] },
                (existing) => {
                    if (mode === ChangeMode.REPLACE && prev && prev.employeeID !== next.employeeID) {
                        if (affectedEmployeeId === prev.employeeID) {
                            return patchListByMode(existing, ChangeMode.REMOVE, prev);
                        }
                        if (affectedEmployeeId === next.employeeID) {
                            return patchListByMode(existing, ChangeMode.ADD, next);
                        }
                        return existing;
                    }
                    return patchListByMode(existing, mode, next, prev);
                }
            );
        });

        // Keep employees index in sync so team-view grouping includes new/moved items immediately.
        queryClient.setQueryData<Employee[] | undefined>(['employees', tenantId], (existing) => {
            if (!existing) return existing;
            return existing.map((employeeEntry) => {
                const employeeAppointments = { ...(employeeEntry.appointments ?? {}) };

                if (mode === ChangeMode.REPLACE && prev && prev.employeeID !== next.employeeID) {
                    if (employeeEntry.id === prev.employeeID) {
                        const nextMap = patchEmployeeAppointmentIndex(employeeAppointments, ChangeMode.REMOVE, prev);
                        return { ...employeeEntry, appointments: nextMap };
                    }
                    if (employeeEntry.id === next.employeeID) {
                        const nextMap = patchEmployeeAppointmentIndex(employeeAppointments, ChangeMode.ADD, next);
                        return { ...employeeEntry, appointments: nextMap };
                    }
                    return employeeEntry;
                }

                if (employeeEntry.id !== next.employeeID) {
                    return employeeEntry;
                }

                const nextMap = patchEmployeeAppointmentIndex(employeeAppointments, mode, next, prev);
                return { ...employeeEntry, appointments: nextMap };
            });
        });

        // Keep selected employee calendar lists in sync without waiting for a refetch.
        const employeeCalendarIds = new Set<string>([next.employeeID, prev?.employeeID].filter(Boolean) as string[]);
        employeeCalendarIds.forEach((employeeId) => {
            queryClient.setQueriesData<Appointment[]>(
                { queryKey: ['employeeCalendarAppointments', tenantId, employeeId] },
                (existing) => {
                    if (mode === ChangeMode.REPLACE && prev && prev.employeeID !== next.employeeID) {
                        if (employeeId === prev.employeeID) {
                            return patchListByMode(existing, ChangeMode.REMOVE, prev);
                        }
                        if (employeeId === next.employeeID) {
                            return patchListByMode(existing, ChangeMode.ADD, next);
                        }
                        return existing;
                    }
                    return patchListByMode(existing, mode, next, prev);
                }
            );
        });

        // Keep team day cards in sync for the affected day keys.
        if (mode === ChangeMode.REPLACE && prev && prev.appointmentDate !== next.appointmentDate) {
            queryClient.setQueriesData<Appointment[]>(
                { queryKey: ['teamDayAppointments', tenantId, prev.appointmentDate] },
                (existing) => patchListByMode(existing, ChangeMode.REMOVE, prev)
            );
            queryClient.setQueriesData<Appointment[]>(
                { queryKey: ['teamDayAppointments', tenantId, next.appointmentDate] },
                (existing) => patchListByMode(existing, ChangeMode.ADD, next)
            );
            return;
        }

        queryClient.setQueriesData<Appointment[]>(
            { queryKey: ['teamDayAppointments', tenantId, next.appointmentDate] },
            (existing) => patchListByMode(existing, mode, next, prev)
        );
    };

    syncCalendarCachesRef.current = syncCalendarCaches;
    pendingCalendarCacheRef.current = pendingCalendarCache;

    useEffect(() => {
        return () => {
            const pending = pendingCalendarCacheRef.current;
            const sync = syncCalendarCachesRef.current;
            if (!pending.length || !sync) {
                return;
            }
            // Flush any deferred cache updates if the view unmounts mid-dialog.
            pending.forEach((entry) => {
                sync(entry.next, entry.mode, entry.prev, entry.options);
            });
        };
    }, []);


    // Filter out cancelled appointments and only show completed ones after they end
    const visibleAppointments = useMemo(() => {
        const now = dayjs();
        return myAppointments
            .filter((appointment) => {
                if (appointment.status === AppointmentStatus.CANCELLED) {
                    return false;
                }
                if (appointment.status === AppointmentStatus.COMPLETE) {
                    const start = dayjs(`${appointment.appointmentDate}${appointment.appointmentTime}`, 'MMDDYYYYHHmm');
                    const end = start.add(appointment.appointmentDuration, 'minute');
                    return end.isBefore(now);
                }
                return true;
            })
            .filter((appointment, index, appointments) => {
                if (!(isBlocker(appointment) && appointment.recurringBreakId)) {
                    return true;
                }

                const dedupeKey = [
                    appointment.recurringBreakId,
                    appointment.appointmentDate,
                    appointment.appointmentTime,
                    appointment.appointmentDuration,
                ].join('|');

                return index === appointments.findIndex((candidate) => (
                    isBlocker(candidate) &&
                    Boolean(candidate.recurringBreakId) &&
                    [
                        candidate.recurringBreakId,
                        candidate.appointmentDate,
                        candidate.appointmentTime,
                        candidate.appointmentDuration,
                    ].join('|') === dedupeKey
                ));
            });
    }, [myAppointments]);

    const appointmentById = useMemo(() => {
        const map = new Map<string, Appointment>();
        visibleAppointments.forEach((a) => map.set(a.id, a));
        return map;
    }, [visibleAppointments]);

    // Build background events for breaks and day-off indicators
    const backgroundEvents = useMemo(() => {
        if (!schedule || !viewStart || !viewEnd) return [] as any[];

        const events: any[] = [];
        let cursor = viewStart.startOf('day');
        const end = viewEnd.endOf('day');

        while (cursor.isBefore(end)) {
            const dateKey = cursor.format('MMDDYYYY');
            const bitString = schedule.get(dateKey);
            if (bitString) {
                const appsForDay = myAppointments.filter(a => a.appointmentDate === dateKey);
                const info = extractWorkHours(appsForDay, bitString);
                const dayStart = cursor.startOf('day');
                const nextDayStart = dayStart.add(1, 'day');

                if (info.isDayOff) {
                    // Render a full-width event banner for complete days off
                    events.push({
                        id: `off-${dateKey}`,
                        title: 'Day Off',
                        start: dayStart.toDate(),
                        end: nextDayStart.toDate(),
                        color: theme.palette.action.disabled,
                        textColor: theme.palette.getContrastText(theme.palette.action.disabled),
                        editable: false,
                        overlap: true,
                    });
                } else {
                    // Shade non-working hours outside of work start/end times
                    const alignToDay = (source: Dayjs) =>
                        dayStart.hour(source.hour()).minute(source.minute()).second(0).millisecond(0);
                    const workStart = alignToDay(info.startTime);
                    const workEnd = alignToDay(info.endTime);
                    const shadeColor = theme.palette.action.disabled;

                    if (workStart.isAfter(dayStart)) {
                        // Shade the early-morning closed hours before work begins
                        events.push({
                            id: `closed-${dateKey}-before`,
                            start: dayStart.toDate(),
                            end: workStart.toDate(),
                            display: 'background',
                            backgroundColor: shadeColor,
                            color: theme.palette.getContrastText(shadeColor),
                            overlap: false,
                        });
                    }
                    if (workEnd.isBefore(nextDayStart)) {
                        // Shade the evening closed hours after the workday ends
                        events.push({
                            id: `closed-${dateKey}-after`,
                            start: workEnd.toDate(),
                            end: nextDayStart.toDate(),
                            display: 'background',
                            backgroundColor: shadeColor,
                            color: theme.palette.getContrastText(shadeColor),
                            overlap: false,
                        });
                    }
                }
            }
            cursor = cursor.add(1, 'day');
        }
        return events;
    }, [schedule, viewStart, viewEnd, myAppointments, theme.palette]);

    // Compute visible time window based on work hours within current view
    useEffect(() => {
        if (!schedule || !viewStart || !viewEnd) return;

        let earliestMinutes: number | null = null;
        let latestMinutes: number | null = null;

        let cursor = viewStart.startOf('day');
        const end = viewEnd.endOf('day');

        while (cursor.isBefore(end)) {
            const dateKey = cursor.format('MMDDYYYY');
            const bitString = schedule.get(dateKey);
            if (bitString) {
                const appsForDay = myAppointments.filter(a => a.appointmentDate === dateKey);
                const info = extractWorkHours(appsForDay, bitString);
                if (!info.isDayOff) {
                    const startMinutes = info.startTime.hour() * 60 + info.startTime.minute();
                    const endMinutes = info.endTime.hour() * 60 + info.endTime.minute();
                    earliestMinutes = earliestMinutes === null || startMinutes < earliestMinutes ? startMinutes : earliestMinutes;
                    latestMinutes = latestMinutes === null || endMinutes > latestMinutes ? endMinutes : latestMinutes;
                }
            }
            cursor = cursor.add(1, 'day');
        }

        // Defaults if no schedule in range
        if (earliestMinutes === null || latestMinutes === null) {
            setSlotMinTime('08:00:00');
            setSlotMaxTime('18:00:00');
            return;
        }

        // Always add a 1-hour buffer before and after and snap to the hour
        const startAdjMinutes = Math.max(0, earliestMinutes - 60);
        const endAdjMinutes = Math.min(24 * 60, latestMinutes + 60);
        const startHour = Math.floor(startAdjMinutes / 60);
        const endHour = Math.floor(endAdjMinutes / 60);
        const toHH = (h: number) => `${String(h).padStart(2, '0')}:00:00`;

        setSlotMinTime(toHH(startHour));
        setSlotMaxTime(toHH(endHour));
    }, [schedule, viewStart, viewEnd, myAppointments]);

    // Update on drag/resize
    const updateMutation = useMutation({
        mutationFn: async ({ original, next }: { original: Appointment; next: Appointment }) => {
            await updateAppointment(original, next, employee.id || original.employeeID, schedule ?? undefined, tenantId);
            await updateAppointmentInEmployeeProfile(original, next, tenantId);
        },
        onMutate: () => {
            setMoveError(null);
        },
        onSuccess: async (_, variables) => {
            if (variables) {
                setPendingCalendarCache((prev) => ([
                    ...prev,
                    {
                        mode: ChangeMode.REPLACE,
                        next: variables.next,
                        prev: variables.original,
                        waitFor: 'move',
                    },
                ]));
            }
            setIsMoveDialogOpen(false);
            const updatedLabel = variables && isBlocker(variables.original) ? 'Break' : 'Appointment';
            showSuccess(`${updatedLabel} updated on the calendar.`);
        },
        onError: (error: any, variables) => {
            console.error('[Calendar] updateMutation failed', error);
            const updatedLabel = variables && isBlocker(variables.original) ? 'break' : 'appointment';
            setMoveError(`Failed to update ${updatedLabel}, are you sure this time is available?`);
            showError(`Failed to update ${updatedLabel} on the calendar.`);
        }
    });
    const createBlockerMutation = useMutation({
        mutationFn: async ({ start, end, title }: { start: Dayjs; end: Dayjs; title: string }) => {
            if (!employee) throw new Error('Missing employee details');
            return await createBlocker({
                employee,
                start,
                end,
                title,
                siteId: tenantId,
                scheduleBitStrings: schedule ?? undefined,
            });
        },
        onMutate: () => {
            setBlockerError(null);
        },
        onSuccess: (blocker) => {
            setPendingCalendarCache((prev) => ([
                ...prev,
                {
                    mode: ChangeMode.ADD,
                    next: blocker,
                    waitFor: 'blockerConfirmation',
                },
            ]));
            setAddBlockerOpen(false);
            setBlockerConfirmation({
                title: 'Blocker Added',
                message: 'Blocker added to the calendar.',
            });
        },
        onError: (error: any) => {
            setBlockerError(error?.message || 'Failed to add blocker');
        },
    });
    const deleteBlockerMutation = useMutation({
        mutationFn: async (blocker: Appointment) => {
            await deleteBlocker(blocker, tenantId);
            return blocker;
        },
        onMutate: () => {
            setBlockerDeleteError(null);
        },
        onSuccess: (blocker) => {
            setPendingCalendarCache((prev) => ([
                ...prev,
                {
                    mode: ChangeMode.REMOVE,
                    next: blocker,
                    options: { serverUpdatedSchedule: false },
                    waitFor: 'blockerConfirmation',
                },
            ]));
            setBlockerDetailsOpen(false);
            setBlockerConfirmation({
                title: 'Blocker Deleted',
                message: 'Blocker removed from the calendar.',
            });
        },
        onError: (error: any) => {
            setBlockerDeleteError(error?.message || 'Failed to delete blocker');
        },
    });

    useEffect(() => {
        if (!pendingCalendarCache.length) {
            return;
        }

        const [nextPending] = pendingCalendarCache;
        const dialogOpen =
            (nextPending.waitFor === 'addAppointment' && addAppointmentOpen) ||
            (nextPending.waitFor === 'move' && isMoveDialogOpen) ||
            (nextPending.waitFor === 'addBlocker' && addBlockerOpen) ||
            (nextPending.waitFor === 'blockerDetails' && blockerDetailsOpen) ||
            (nextPending.waitFor === 'blockerConfirmation' && Boolean(blockerConfirmation));

        if (dialogOpen) {
            return;
        }

        // Apply updates only after the blocking dialog is closed.
        syncCalendarCaches(
            nextPending.next,
            nextPending.mode,
            nextPending.prev,
            nextPending.options
        );
        setPendingCalendarCache((prev) => prev.slice(1));
    }, [
        pendingCalendarCache,
        addAppointmentOpen,
        isMoveDialogOpen,
        addBlockerOpen,
        blockerDetailsOpen,
        blockerConfirmation,
    ]);


    const formatAppointmentRange = (appointment: Appointment) => {
        const start = dayjs(`${appointment.appointmentDate}${appointment.appointmentTime}`, 'MMDDYYYYHHmm');
        const end = start.add(appointment.appointmentDuration, 'minute');
        return `${start.format('dddd, MMMM D [at] h:mm A')} - ${end.format('h:mm A')}`;
    };

    const toEvent = (a: Appointment) => {
        const start = dayjs(`${a.appointmentDate}${a.appointmentTime}`, 'MMDDYYYYHHmm');
        const end = start.add(a.appointmentDuration, 'minute');
        if (isBlocker(a)) {
            const isRecurringBlocker = Boolean(a.recurringBreakId);
            return {
                id: a.id,
                title: `${a.notes || a.appointmentTitle || 'Unavailable'}`,
                start: start.toDate(),
                end: end.toDate(),
                backgroundColor: theme.palette.warning.main,
                borderColor: theme.palette.warning.dark,
                textColor: theme.palette.getContrastText(theme.palette.warning.main),
                extendedProps: { appointment: a, isBlocker: true, isRecurringBlocker },
                editable: !isRecurringBlocker,
                durationEditable: !isRecurringBlocker,
                startEditable: !isRecurringBlocker,
            } as any;
        }
        const isComplete = a.status === AppointmentStatus.COMPLETE;
        const bg = isComplete
            ? theme.palette.grey[400]
            : theme.palette.secondary.light;
        const border = isComplete
            ? theme.palette.grey[500]
            : theme.palette.secondary.main;
        const txt = theme.palette.secondary.contrastText;
        return {
            id: a.id,
            title: `${a.appointmentTitle} — ${a.customerFirstName} ${a.customerLastName}`,
            start: start.toDate(),
            end: end.toDate(),
            backgroundColor: bg,
            borderColor: border,
            textColor: txt,
            extendedProps: { appointment: a },
        } as any;
    };

    const onEventClick = (arg: EventClickArg) => {
        const canHandleInline = !readOnly && !hideDialogs;
        if (!canHandleInline && !onAppointmentClick) {
            return;
        }
        const id = arg.event.id;
        const appt = appointmentById.get(id) || null;
        if (onAppointmentClick && appt) {
            onAppointmentClick(appt);
            return;
        }
        if (appt && isBlocker(appt)) {
            setSelectedBlocker(appt);
            setBlockerDetailsOpen(true);
            return;
        }
        setSelected(appt);
        setOpen(!!appt);
    };

    const editFromEvent = (id: string, start: Date, end: Date): Appointment | null => {
        const orig = appointmentById.get(id);
        if (!orig) return null;
        const s = dayjs(start);
        const e = dayjs(end);
        const next: Appointment = {
            ...orig,
            appointmentDate: s.format('MMDDYYYY'),
            appointmentTime: s.format('HHmm'),
            appointmentDuration: Math.max(5, e.diff(s, 'minute')), // keep min granularity
            lastUpdater: employee.id || orig.lastUpdater,
        };
        return next;
    };

    const onEventDrop = (arg: EventDropArg) => {
        const next = editFromEvent(arg.event.id, arg.event.start!, arg.event.end || arg.event.start!);
        const orig = appointmentById.get(arg.event.id);
        if (orig && isBlocker(orig) && orig.recurringBreakId) {
            arg.revert();
            return;
        }
        if (orig && next) {
            arg.revert();
            setMoveConfirmation({ original: orig, next, action: 'move' });
            setIsMoveDialogOpen(true);
            setMoveError(null);
        }
    };
    const onEventResize = (arg: EventResizeDoneArg) => {
        const next = editFromEvent(arg.event.id, arg.event.start!, arg.event.end || arg.event.start!);
        const orig = appointmentById.get(arg.event.id);
        if (orig && isBlocker(orig) && orig.recurringBreakId) {
            arg.revert();
            return;
        }
        if (orig && next) {
            arg.revert();
            setMoveConfirmation({ original: orig, next, action: 'resize' });
            setIsMoveDialogOpen(true);
            setMoveError(null);
        }
    };
    const handleDateClick = (info: DateClickArg) => {
        const clicked = dayjs(info.date);
        handleSharedDateChange(clicked);
        if (!readOnly && !info.allDay && info.view.type.startsWith('timeGrid')) {
            openCreationDialog(clicked);
        }
    };
    const handleBlockerStartInput = (value: string) => {
        const nextStart = snapToInterval(dayjs(value));
        if (!nextStart.isValid()) return;
        setBlockerForm((prev) => {
            const adjustedEnd = prev.end.isAfter(nextStart)
                ? prev.end
                : nextStart.add(SELECTION_TIME_INTERVAL, 'minute');
            return { ...prev, start: nextStart, end: adjustedEnd };
        });
    };
    const handleBlockerEndInput = (value: string) => {
        const nextEnd = snapToInterval(dayjs(value));
        if (!nextEnd.isValid()) return;
        setBlockerForm((prev) => {
            const safeEnd = nextEnd.isAfter(prev.start)
                ? nextEnd
                : prev.start.add(SELECTION_TIME_INTERVAL, 'minute');
            return { ...prev, end: safeEnd };
        });
    };
    const handleCreateBlocker = () => {
        if (!blockerForm.start || !blockerForm.end) return;
        const sanitizedTitle = blockerForm.title.trim() || BLOCKER_DEFAULT_TITLE;
        createBlockerMutation.mutate({
            start: blockerForm.start,
            end: blockerForm.end,
            title: sanitizedTitle,
        });
    };
    const handleCloseBlockerModal = () => {
        setAddBlockerOpen(false);
        setBlockerError(null);
    };
    const handleCloseManageDialog = () => {
        setOpen(false);
    };
    const handleDeleteBlocker = () => {
        if (!selectedBlocker) return;
        deleteBlockerMutation.mutate(selectedBlocker);
    };
    const handleCloseBlockerDetails = () => {
        setBlockerDetailsOpen(false);
    };
    const handleConfirmAppointmentMove = () => {
        if (!moveConfirmation) return;
        updateMutation.mutate({ original: moveConfirmation.original, next: moveConfirmation.next });
    };
    const handleCancelAppointmentMove = () => {
        setIsMoveDialogOpen(false);
        setMoveError(null);
    };
    const handleShowCurrentDayBitstring = () => {
        if (!viewStart) return;
        const dateKey = viewStart.format('MMDDYYYY');
        const dateLabel = viewStart.format('dddd, MMMM D, YYYY');
        const bitstring = schedule?.get(dateKey) ?? null;
        setBitstringDialogState({ dateLabel, bitstring, dateKey });
    };
    const handleCloseBitstringDialog = () => {
        setBitstringDialogState(null);
    };

    // Disallow edits for past appointments and drops into the past
    const eventAllow = (dropInfo: any, draggedEvent: any) => {
        if (readOnly) return false;
        const now = dayjs();
        // If original event has already ended, block edits
        if (draggedEvent) {
            const orig: Appointment | undefined = appointmentById.get(draggedEvent.id);
            if (orig) {
                if (isBlocker(orig) && orig.recurringBreakId) return false;
                const origStart = dayjs(`${orig.appointmentDate}${orig.appointmentTime}`, 'MMDDYYYYHHmm');
                const origEnd = origStart.add(orig.appointmentDuration, 'minute');
                if (origEnd.isBefore(now)) return false;
            }
        }
        // Prevent moving into the past
        if (dayjs(dropInfo.start).isBefore(now)) return false;
        return true;
    };

    // Keep mobile or day-only view locked to day view
    useEffect(() => {
        const api = calendarRef.current?.getApi();
        if (!api) return;

        if (isMobile || onlyDayView) {
            setCurrentViewType('timeGridDay');
            if (api.view.type !== 'timeGridDay') api.changeView('timeGridDay');
        }
    }, [isMobile, onlyDayView]);

    // Sync external shared date -> calendar view
    useEffect(() => {
        if (!activeSharedDate) return;
        const api = calendarRef.current?.getApi();
        if (!api) return;
        const currentStart = dayjs(api.view.currentStart);
        if (!currentStart.isSame(activeSharedDate, 'day')) {
            api.gotoDate(activeSharedDate.toDate());
        }
    }, [activeSharedDate]);


    // const canShowBitstringButton =
    //     !readOnly &&
    //     (isLocalEnv || WEBSITE_ID === 'example2' || WEBSITE_ID === 'example1') &&
    //     currentViewType === 'timeGridDay';
    const isDayView = currentViewType === 'timeGridDay';
    const canShowEditWorkHoursInCreateDialog = currentViewType === 'timeGridWeek' && Boolean(onWorkHoursClick);
    const showWorkHoursSummary = isDayView && Boolean(workHoursLabel) && Boolean(onWorkHoursClick);

    if (isError) {
        const errorBody = (
            <Alert severity="error">
                Failed to load calendar data. Please refresh the page and try again.
            </Alert>
        );
        return embedded ? (
            <Box sx={{ pt: 4 }}>{errorBody}</Box>
        ) : (
            <Box sx={{ pt: 2 }}>
                {errorBody}
            </Box>
        );
    }

    const hasStickyHeader = stickyHeaderTop !== undefined;
    const handleCalendarPrevious = () => calendarRef.current?.getApi().prev();
    const handleCalendarNext = () => calendarRef.current?.getApi().next();
    const scrollCalendarToNow = () => {
        const api = calendarRef.current?.getApi();
        if (!api?.view.type.startsWith('timeGrid')) return;

        const now = dayjs.tz();
        const targetTime = now.subtract(45, 'minute');
        api.scrollToTime((targetTime.isSame(now, 'day') ? targetTime : now.startOf('day')).format('HH:mm:ss'));

        const nowIndicator = calendarContainerRef.current?.querySelector(
            '.fc-timegrid-now-indicator-line, .fc-timegrid-now-indicator-arrow'
        );
        nowIndicator?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    const handleCalendarToday = () => {
        const api = calendarRef.current?.getApi();
        if (!api) return;

        api.today();
        if (typeof window === 'undefined') {
            scrollCalendarToNow();
            return;
        }

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(scrollCalendarToNow);
        });
        window.setTimeout(scrollCalendarToNow, 150);
    };
    const handleCalendarWeek = () => calendarRef.current?.getApi().changeView('timeGridWeek');
    const handleCalendarMonth = () => calendarRef.current?.getApi().changeView('dayGridMonth');
    const calendarNavigation = !hideToolbar && (
        <CalendarNavigationBar
            title={calendarTitle}
            currentViewType={currentViewType}
            onlyDayView={onlyDayView}
            onPrevious={handleCalendarPrevious}
            onNext={handleCalendarNext}
            onToday={handleCalendarToday}
            onWeek={handleCalendarWeek}
            onMonth={handleCalendarMonth}
            onTitleClick={onCalendarTitleClick}
        />
    );
    const workHoursSummary = showWorkHoursSummary && onWorkHoursClick && (
        <WorkHoursSummaryBar
            hoursLabel={workHoursLabel!}
            breakLabel={workHoursBreakLabel}
            embeddedInCard={hasStickyHeader}
            onClick={onWorkHoursClick}
        />
    );

    const calendarBody = (
        <>
            <Box ref={calendarContainerRef} sx={(theme) => ({
                mt: embedded ? 0 : 0.5,
                mb: embedded ? 0 : 2,
                '& .fc': {
                    '--fc-button-bg-color': theme.palette.secondary.main,
                    '--fc-button-border-color': theme.palette.secondary.main,
                    '--fc-button-text-color': theme.palette.getContrastText(theme.palette.secondary.main),
                    '--fc-button-hover-bg-color': theme.palette.secondary.dark,
                    '--fc-button-hover-border-color': theme.palette.secondary.dark,
                    '--fc-button-active-bg-color': theme.palette.secondary.dark,
                    '--fc-button-active-border-color': theme.palette.secondary.dark,
                    '--fc-font-family': theme.typography.fontFamily,
                    '--fc-page-font-size': theme.typography.body1.fontSize,
                    '--fc-small-font-size': theme.typography.caption.fontSize,
                    '--fc-today-bg-color': 'transparent', // remove default yellow "today" background
                },
                '& .fc *': { fontFamily: (theme.typography as any).fontFamily },
                // Increase slot and event heights for timeGrid for better readability
                '& .fc .fc-timegrid-slot': {
                    height: { xs: '52px', sm: '40px' },
                },
                '& .fc .fc-timegrid-slot-label': {
                    height: { xs: '52px', sm: '40px' },
                    lineHeight: { xs: '52px', sm: '40px' },
                },
                '& .fc .fc-timegrid-event': {
                    minHeight: { xs: 40, sm: 32 },
                },
            })}>
                {hasStickyHeader && (stickyHeaderControls || calendarNavigation || workHoursSummary) ? (
                    <CalendarHeaderCard
                        stickyTop={stickyHeaderTop}
                        controls={stickyHeaderControls}
                        navigation={calendarNavigation}
                        workHours={workHoursSummary}
                    />
                ) : (
                    <>
                        {calendarNavigation}
                        {workHoursSummary}
                    </>
                )}
                {isMobile && (
                    <>
                        {/* Force day view on mobile */}
                    </>
                )}
                {/* {canShowBitstringButton && (
                    <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, mb: 1 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={handleShowCurrentDayBitstring}
                            disabled={!viewStart}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            View Day Bitstring
                        </Button>
                    </Box>
                )} */}
                <FullCalendar
                    ref={calendarRef as any}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    initialView={onlyDayView || isMobile ? 'timeGridDay' : 'timeGridWeek'}
                    headerToolbar={false}
                    height="auto"
                    allDaySlot={false}
                    nowIndicator
                    selectable={!readOnly}
                    editable={!readOnly}
                    eventDurationEditable={!readOnly}
                    eventResizableFromStart={!readOnly}
                    slotDuration={{ minutes: SELECTION_TIME_INTERVAL }}
                    snapDuration={isMobile ? { minutes: SELECTION_TIME_INTERVAL } : { minutes: TIME_INTERVAL }} // finer snapping on desktop for easier drag/resize
                    eventAllow={readOnly ? undefined : eventAllow}
                    eventDrop={readOnly ? undefined : onEventDrop}
                    eventResize={readOnly ? undefined : onEventResize}
                    eventClick={(readOnly || hideDialogs) && !onAppointmentClick ? undefined : onEventClick}
                    dateClick={handleDateClick}
                    events={[...visibleAppointments.map(toEvent), ...backgroundEvents]}
                    initialDate={activeSharedDate?.toDate()}
                    slotMinTime={slotMinTime}
                    slotMaxTime={slotMaxTime}
                    datesSet={(arg: DatesSetArg) => {
                        const start = dayjs(arg.start);
                        const end = dayjs(arg.end);
                        const title = formatTitle(start, end, arg.view.type);
                        setCalendarTitle(title);
                        setCurrentViewType(arg.view.type);
                        setViewStart(start);
                        setViewEnd(end);
                        // Only push shared date when in day view to avoid jumping on week/month navigation
                        if (arg.view.type === 'timeGridDay') {
                            handleSharedDateChange(start);
                        }
                    }}
                />
            </Box>
            {!readOnly && !hideCreateButton && (
                <Portal>
                    <Fab
                        color="secondary"
                        aria-label="Add calendar item"
                        onClick={() => openCreationDialog()}
                        sx={{
                            position: 'fixed',
                            bottom: 24,
                            right: 24,
                            zIndex: (theme) => theme.zIndex.drawer + 2,
                        }}
                    >
                        <AddIcon />
                    </Fab>
                </Portal>
            )}
            {!readOnly && !hideDialogs && (
                <Dialog
                    open={createDialogOpen}
                    onClose={handleCloseCreateDialog}
                    maxWidth="xs"
                    fullWidth
                    sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
                >
                    <DialogTitle sx={{ pr: 6 }}>
                        Add Calendar Item
                        <IconButton color="secondary" onClick={handleCloseCreateDialog} sx={{ position: 'absolute', right: 8, top: 8 }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary">
                            Choose what to add{createAnchor ? ` on ${createAnchor.format('MMM D, h:mm A')}` : ''}.
                        </Typography>
                        {createEmployeeOptions && createEmployeeOptions.length > 1 && onSelectCreateEmployee && (
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={calendarResourceSingular}
                                value={selectedCreateEmployeeId ?? ''}
                                onChange={(event) => onSelectCreateEmployee(event.target.value)}
                                sx={{ mt: 2 }}
                            >
                                {createEmployeeOptions.map((employeeOption) => (
                                    <MenuItem key={employeeOption.id} value={employeeOption.id}>
                                        {employeeOption.firstName} {employeeOption.lastName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        {canShowEditWorkHoursInCreateDialog && (
                            <Button variant="outlined" onClick={handleSelectEditWorkHours} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                Edit Hours
                            </Button>
                        )}
                        {createDialogMode === 'full' && (
                            <Button variant="contained" onClick={handleSelectCreateBlocker} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                Unavailability
                            </Button>
                        )}
                        <Button variant="contained" color="secondary" onClick={handleSelectCreateAppointment} sx={{ textTransform: 'none', fontWeight: 600 }}>
                            Appointment
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            <BitstringDebugDialog
                open={!!bitstringDialogState}
                onClose={handleCloseBitstringDialog}
                dateLabel={bitstringDialogState?.dateLabel}
                dateKey={bitstringDialogState?.dateKey}
                bitstring={bitstringDialogState?.bitstring}
            />
            {/* Confirm change dialog */}
            {!readOnly && !hideDialogs && (
                <Dialog
                    open={isMoveDialogOpen}
                    onClose={handleCancelAppointmentMove}
                    fullWidth
                    maxWidth="xs"
                    sx={{ '& .MuiDialog-paper': { borderRadius: 2, width: { xs: '95%', sm: 'auto' } } }}
                    TransitionProps={{
                        onExited: () => setMoveConfirmation(null),
                    }}
                >
                    <DialogTitle sx={{ pr: 6 }}>
                        Confirm {moveConfirmation?.action === 'resize' ? 'Update' : 'Move'}
                        <IconButton color="secondary" onClick={handleCancelAppointmentMove} sx={{ position: 'absolute', right: 8, top: 8 }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    {moveConfirmation && (
                        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography variant="body1">
                                {moveConfirmation.action === 'resize' ? (
                                    <>
                                        Update <strong>{moveConfirmation.original.appointmentTitle}</strong> to run from{' '}
                                        {formatAppointmentRange(moveConfirmation.next)}?
                                    </>
                                ) : (
                                    <>
                                        Move <strong>{moveConfirmation.original.appointmentTitle}</strong> from{' '}
                                        {formatAppointmentRange(moveConfirmation.original)} to {formatAppointmentRange(moveConfirmation.next)}?
                                    </>
                                )}
                            </Typography>
                            {/* Upcoming appointments notify customers only when the start changes. */}
                            {(moveConfirmation.original.status === AppointmentStatus.UPCOMING &&
                                (moveConfirmation.original.appointmentDate !== moveConfirmation.next.appointmentDate ||
                                    moveConfirmation.original.appointmentTime !== moveConfirmation.next.appointmentTime)) && (
                                    <Typography variant="body2" color="text.secondary">
                                        Customer will be notified of this change.
                                    </Typography>
                                )}
                            {moveError && <Alert severity="error">{moveError}</Alert>}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleCancelAppointmentMove}
                                    disabled={updateMutation.isPending}
                                    sx={{ flex: 1, textTransform: 'none' }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleConfirmAppointmentMove}
                                    disabled={updateMutation.isPending}
                                    sx={{ flex: 1, textTransform: 'none', fontWeight: 600 }}
                                >
                                    {updateMutation.isPending
                                        ? moveConfirmation.action === 'resize'
                                            ? 'Updating...'
                                            : 'Moving...'
                                        : 'Confirm'}
                                </Button>
                            </Stack>
                        </DialogContent>
                    )}
                </Dialog>
            )}
            {!readOnly && !hideDialogs && (
                <Dialog
                    open={open}
                    onClose={handleCloseManageDialog}
                    fullWidth
                    maxWidth="sm"
                    TransitionComponent={Fade}
                    TransitionProps={{
                        timeout: 220,
                        onExited: () => setSelected(null),
                    }}
                    sx={{
                        '& .MuiDialog-paper': {
                            borderRadius: 2,
                            width: '100%',
                            m: 0,
                            mx: 1,
                            height: 'auto',
                            maxHeight: { xs: '92dvh', sm: 'calc(100% - 64px)' },
                        }
                    }}
                >
                    <DialogTitle sx={{ pr: 6 }}>
                        Manage Appointment
                        <IconButton color="secondary" onClick={handleCloseManageDialog} sx={{ position: 'absolute', right: 12, top: 12 }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ p: { xs: 0, sm: 2 } }}>
                        {selected && (
                            <AppointmentCard
                                appointment={selected}
                                fullWidth
                                defaultExpanded={isMobile}
                                renderAsDialogContent
                                employeeIdOverride={employee.id}
                                scheduleOverride={schedule ?? undefined}
                                appointmentIdsOverride={appointmentIds}
                                updateCurrentEmployeeIndexCache={updateCurrentEmployeeIndexCache}
                                showSuccess={showSuccess}
                                showError={showError}
                                onHandoffSuccess={() => setHandoffSuccessDialogOpen(true)}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            )}
            <Dialog
                open={handoffSuccessDialogOpen}
                onClose={() => setHandoffSuccessDialogOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Appointment Handed Off</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        The appointment was successfully handed off to the selected staff member.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setHandoffSuccessDialogOpen(false)}
                        color="primary"
                        variant="contained"
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
            {!readOnly && !hideDialogs && (
                <Dialog open={addBlockerOpen} onClose={handleCloseBlockerModal} fullWidth maxWidth="xs"
                    sx={{ '& .MuiDialog-paper': { borderRadius: 2, width: { xs: '95%', sm: 'auto' } } }}
                >
                    <DialogTitle sx={{ pr: 6 }}>
                        Add Blocker
                        <IconButton color="secondary" onClick={handleCloseBlockerModal} sx={{ position: 'absolute', right: 8, top: 8 }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField
                                label="Blocker Title"
                                value={blockerForm.title}
                                onChange={(e) => setBlockerForm((prev) => ({ ...prev, title: e.target.value }))}
                                fullWidth
                            />
                            <TextField
                                label="Start time"
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                value={formatInputValue(blockerForm.start)}
                                onChange={(e) => handleBlockerStartInput(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="End time"
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                value={formatInputValue(blockerForm.end)}
                                onChange={(e) => handleBlockerEndInput(e.target.value)}
                                fullWidth
                            />
                            {blockerError && <Alert severity="error">{blockerError}</Alert>}
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleCreateBlocker}
                                disabled={!blockerForm.end.isAfter(blockerForm.start) || createBlockerMutation.isPending}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                {createBlockerMutation.isPending ? 'Adding...' : 'Add Blocker'}
                            </Button>
                        </Stack>
                    </DialogContent>
                </Dialog>
            )}
            {!readOnly && !hideDialogs && (
                <Dialog open={blockerDetailsOpen} onClose={handleCloseBlockerDetails} fullWidth maxWidth="sm"
                    TransitionProps={{
                        onExited: () => {
                            setSelectedBlocker(null);
                            setBlockerDeleteError(null);
                        },
                    }}
                    sx={{ '& .MuiDialog-paper': { borderRadius: 2, width: { xs: '95%', sm: 'auto' } } }}
                >
                    <DialogTitle sx={{ pr: 6 }}>
                        Details
                        <IconButton color="secondary" onClick={handleCloseBlockerDetails} sx={{ position: 'absolute', right: 8, top: 8 }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {selectedBlocker && <BlockerCard blocker={selectedBlocker} />}
                        {blockerDeleteError && <Alert severity="error">{blockerDeleteError}</Alert>}
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleDeleteBlocker}
                            disabled={deleteBlockerMutation.isPending}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {deleteBlockerMutation.isPending ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogContent>
                </Dialog>
            )}
            {!readOnly && !hideDialogs && (
                <Dialog
                    open={Boolean(blockerConfirmation)}
                    onClose={() => setBlockerConfirmation(null)}
                    fullWidth
                    maxWidth="xs"
                    sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
                >
                    <DialogTitle sx={{ pr: 6 }}>
                        {blockerConfirmation?.title}
                        <IconButton color="secondary" onClick={() => setBlockerConfirmation(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary">
                            {blockerConfirmation?.message}
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button variant="contained" onClick={() => setBlockerConfirmation(null)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {!readOnly && !hideDialogs && (
                <Dialog
                    open={addAppointmentOpen}
                    onClose={handleCloseAppointmentModal}
                    fullWidth
                    maxWidth="sm"
                    fullScreen={isMobile}
                    TransitionProps={{
                        onExited: () => {
                            setAppointmentDraft(null);
                            setInternalBookingConfirmation(null);
                        },
                    }}
                    sx={{
                        height: isMobile ? '100%' : '95%',
                        '& .MuiDialog-paper': {
                            borderRadius: isMobile ? 0 : 2,
                            height: isMobile ? '100%' : '95%',
                            maxHeight: 'none',
                            margin: isMobile ? 0 : 'auto',
                        },
                    }}
                >
                    <IconButton
                        onClick={handleCloseAppointmentModal}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            color: '#000',
                            zIndex: 1,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent sx={{ padding: '0 20px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {internalBookingConfirmation ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                                    <BookingConfirmationDetails
                                        bookingConfirmation={internalBookingConfirmation}
                                        attemptedCardPayment={false}
                                        paid={false}
                                        isInternal
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        bottom: 0,
                                        width: '100%',
                                        py: 1,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        backgroundColor: 'background.paper',
                                        zIndex: 2,
                                    }}
                                >
                                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                        <Button
                                            variant="text"
                                            onClick={handleCloseAppointmentModal}
                                            sx={{ width: '100%', borderRadius: '4px', fontWeight: 600, fontSize: '1rem', py: 1.2 }}
                                        >
                                            Close
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            appointmentDraft && (
                                <BookNowDialog
                                    appointment={appointmentDraft}
                                    handleAppointment={handleInternalAppointmentChange}
                                    services={servicesQuery.data ?? []}
                                    handleCloseBooking={handleCloseAppointmentModal}
                                    enabledCustomerSMS={false}
                                    enabledEmployeeSMS={false}
                                    enabledPayments={false}
                                    requirePaymentForBooking={false}
                                    minimumBookingNoticeHours={0}
                                    variant="internal"
                                    allowedEmployeeIds={internalBookingAllowedEmployeeIds ?? (employeeId ? [employeeId] : undefined)}
                                    preferredEmployeeId={internalBookingPreferredEmployeeId ?? employeeId}
                                    onBookingSuccess={handleInternalBookingSuccess}
                                    scheduleCacheOverride={schedule ?? null}
                                    scheduleCacheOverrideEmployeeId={employeeId}
                                    employeesOverride={internalBookingEmployeesOverride ?? (employee ? [employee] : [])}
                                />
                            )
                        )}
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
    return <Box>{calendarBody}</Box>;
};

const CalendarScreen: React.FC = () => {
    const { tenantId } = useTenant();
    const APPOINTMENT_ENTITY_MAX_AGE_MS = 1000 * 60 * 5;
    const {
        sharedDate,
        onSharedDateChange,
        prefetchedData,
        scheduleViewMode,
        onScheduleViewModeChange,
        selectedEmployeeId,
        onSelectedEmployeeIdChange,
    } = useScheduleHubContext();
    const { employeesQuery } = useAdminData();
    const queryClient = useQueryClient();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [manageScheduleOpen, setManageScheduleOpen] = useState(false);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [datePickerAnchorEl, setDatePickerAnchorEl] = useState<HTMLElement | null>(null);
    const [profilePictures, setProfilePictures] = useState<Record<string, string>>({});
    const { showSuccess, showError, snackbar } = useResultSnackbar({
        successTitle: 'Schedule Updated!',
        errorTitle: 'Something Went Wrong',
        autoHideDuration: 2500,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
    });
    const {
        employeeQuery,
        hydrateAppointmentsQuery,
        scheduleQuery,
        appointmentIds,
        myAppointments,
    } = prefetchedData;
    const businessConfigQuery = useQuery({
        queryKey: ['businessConfig', tenantId],
        queryFn: () => getCurrentBusinessConfig(tenantId),
    });
    const currentEmployee = employeeQuery.data ?? null;
    const bookingUxConfig = resolveAdminBookingUxConfig(businessConfigQuery.data ?? null);
    const isResourceMode = bookingUxConfig.bookingModel === 'resource';
    const resourceSingular = bookingUxConfig.terminology.resourceSingular;
    const resourcePlural = bookingUxConfig.terminology.resourcePlural;
    const ownerId = businessConfigQuery.data?.paymentInfo?.owner?.trim() || '';
    const dateKey = sharedDate.format('MMDDYYYY');
    const canShowEmployeePicker = currentEmployee?.admin === true;
    const permittedEmployees = useMemo(() => {
        const employees = employeesQuery.data ?? [];
        const baseEmployees = (isResourceMode && ownerId)
            ? employees.filter((employee) => employee.id !== ownerId)
            : employees;
        const sortedBaseEmployees = [...baseEmployees].sort((left, right) =>
            `${left.firstName} ${left.lastName}`.localeCompare(`${right.firstName} ${right.lastName}`)
        );
        if (!currentEmployee) return [] as Employee[];
        if (currentEmployee.admin || (isResourceMode && currentEmployee.id === ownerId)) return sortedBaseEmployees;
        return sortedBaseEmployees.filter((employee) => employee.id === currentEmployee.id);
    }, [currentEmployee, employeesQuery.data, isResourceMode, ownerId]);
    const canShowTeamToggle = currentEmployee?.admin === true && permittedEmployees.length >= 2;
    const selectedEmployee = useMemo(
        () => permittedEmployees.find((employee) => employee.id === selectedEmployeeId) ?? null,
        [permittedEmployees, selectedEmployeeId]
    );

    useEffect(() => {
        if (!canShowTeamToggle && scheduleViewMode !== 'perEmployee') {
            onScheduleViewModeChange('perEmployee');
        }
    }, [canShowTeamToggle, scheduleViewMode, onScheduleViewModeChange]);

    useEffect(() => {
        if (isMobile && scheduleViewMode !== 'perEmployee') {
            onScheduleViewModeChange('perEmployee');
        }
    }, [isMobile, onScheduleViewModeChange, scheduleViewMode]);

    useEffect(() => {
        if (permittedEmployees.length === 0) return;
        const stillExists = permittedEmployees.some((employee) => employee.id === selectedEmployeeId);
        if (!selectedEmployeeId || !stillExists) {
            onSelectedEmployeeIdChange(permittedEmployees[0].id);
        }
    }, [onSelectedEmployeeIdChange, permittedEmployees, selectedEmployeeId]);

    useEffect(() => {
        let isMounted = true;
        const fetchProfilePictures = async () => {
            if (permittedEmployees.length === 0) {
                setProfilePictures({});
                return;
            }

            const newPictures: Record<string, string> = {};
            await Promise.all(
                permittedEmployees.map(async (employee) => {
                    const picturePath = employee.profilePictureImagePath;
                    if (!picturePath) return;
                    try {
                        const url = await getCachedAssetUrl(
                            picturePath,
                            () => getImageFromStorage(picturePath)
                        );
                        if (url) {
                            newPictures[employee.id] = url;
                        }
                    } catch (error) {
                        console.error('Failed to load employee profile picture', error);
                    }
                })
            );

            if (isMounted) {
                setProfilePictures(newPictures);
            }
        };

        fetchProfilePictures();
        return () => {
            isMounted = false;
        };
    }, [permittedEmployees]);

    const selectedEmployeeAllAppointmentIds = useMemo(() => {
        if (!selectedEmployee?.appointments) return [] as string[];
        const ids = new Set<string>();
        Object.values(selectedEmployee.appointments).forEach((entry) => {
            if (!Array.isArray(entry)) return;
            entry.forEach((id) => {
                if (typeof id === 'string' && id.trim().length > 0) {
                    ids.add(id);
                }
            });
        });
        return Array.from(ids).sort();
    }, [selectedEmployee?.appointments]);

    const loadAppointmentsFromCacheFirst = async (
        ids: string[],
        debugLabel: string,
    ): Promise<Appointment[]> => {
        const orderedIds = Array.from(
            new Set(ids.filter((id): id is string => typeof id === 'string' && id.trim().length > 0))
        );
        if (orderedIds.length === 0) {
            return [];
        }

        const byId = new Map<string, Appointment>();
        const missingIds: string[] = [];
        const staleIds: string[] = [];
        const now = Date.now();

        orderedIds.forEach((id) => {
            const key = appointmentKey(tenantId, id);
            const cached = queryClient.getQueryData<Appointment>(key);
            if (cached) {
                byId.set(id, cached);
                const state = queryClient.getQueryState<Appointment>(key);
                const dataUpdatedAt = state?.dataUpdatedAt ?? 0;
                if (!dataUpdatedAt || now - dataUpdatedAt > APPOINTMENT_ENTITY_MAX_AGE_MS) {
                    staleIds.push(id);
                }
            } else {
                missingIds.push(id);
            }
        });

        const idsToFetch = Array.from(new Set([...missingIds, ...staleIds]));
        if (idsToFetch.length > 0) {
            let fetched: Appointment[] = [];
            try {
                fetched = await getAppointmentsByIds(idsToFetch, tenantId, debugLabel);
            } catch (error) {
                console.warn('Failed to refresh appointment cache entries from Firestore.', error);
                throw error;
            }
            const fetchedIds = new Set<string>();
            fetched.forEach((appointment) => {
                fetchedIds.add(appointment.id);
                byId.set(appointment.id, appointment);
                queryClient.setQueryData(appointmentKey(tenantId, appointment.id), appointment);
            });

            // If an appointment doc no longer exists, drop stale cache so it doesn't reappear as a ghost row.
            idsToFetch.forEach((id) => {
                if (fetchedIds.has(id)) return;
                byId.delete(id);
                queryClient.removeQueries({ queryKey: appointmentKey(tenantId, id), exact: true });
            });
        }

        return orderedIds
            .map((id) => byId.get(id))
            .filter((appointment): appointment is Appointment => Boolean(appointment));
    };

    const selectedEmployeeAppointmentsQuery = useQuery<Appointment[]>({
        queryKey: ['employeeCalendarAppointments', tenantId, selectedEmployee?.id, selectedEmployeeAllAppointmentIds.join('|')],
        queryFn: () => loadAppointmentsFromCacheFirst(selectedEmployeeAllAppointmentIds, 'calendar-screen'),
        enabled:
            Boolean(selectedEmployee?.id) &&
            selectedEmployee?.id !== currentEmployee?.id &&
            scheduleViewMode === 'perEmployee',
        staleTime: 1000 * 60 * 5,
    });

    const selectedEmployeeScheduleQuery = useQuery<Map<string, string>>({
        queryKey: ['employeeSchedule', tenantId, selectedEmployee?.id],
        queryFn: () => fetchEmployeeSchedule(selectedEmployee!.id, tenantId),
        enabled:
            Boolean(selectedEmployee?.id) &&
            selectedEmployee?.id !== currentEmployee?.id &&
            scheduleViewMode === 'perEmployee',
        staleTime: 1000 * 60 * 20,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const appointmentIdsByEmployeeForDate = useMemo(() => {
        const map = new Map<string, string[]>();
        const uniqueIds = new Set<string>();

        permittedEmployees.forEach((employee) => {
            const ids = Array.isArray(employee.appointments?.[dateKey])
                ? employee.appointments[dateKey].filter((id) => typeof id === 'string' && id.trim().length > 0)
                : [];
            map.set(employee.id, ids);
            ids.forEach((id) => uniqueIds.add(id));
        });

        return {
            map,
            allIds: Array.from(uniqueIds).sort(),
        };
    }, [dateKey, permittedEmployees]);

    const teamDayAppointmentsQuery = useQuery<Appointment[]>({
        queryKey: ['teamDayAppointments', tenantId, dateKey, appointmentIdsByEmployeeForDate.allIds.join('|')],
        queryFn: () => loadAppointmentsFromCacheFirst(appointmentIdsByEmployeeForDate.allIds, 'calendar-team-view'),
        enabled: scheduleViewMode === 'team',
        staleTime: 1000 * 60 * 5,
    });

    const appointmentsByIdForTeamDay = useMemo(() => {
        const map = new Map<string, Appointment>();
        (teamDayAppointmentsQuery.data ?? []).forEach((appointment) => map.set(appointment.id, appointment));
        return map;
    }, [teamDayAppointmentsQuery.data]);

    const teamDayAppointmentsByEmployee = useMemo(() => {
        const map = new Map<string, Appointment[]>();
        permittedEmployees.forEach((employee) => {
            const ids = appointmentIdsByEmployeeForDate.map.get(employee.id) ?? [];
            const appointments = ids
                .map((id) => appointmentsByIdForTeamDay.get(id))
                .filter((appointment): appointment is Appointment => Boolean(appointment));
            appointments.sort((a, b) => Number(a.appointmentTime) - Number(b.appointmentTime));
            map.set(employee.id, appointments);
        });
        return map;
    }, [appointmentIdsByEmployeeForDate.map, appointmentsByIdForTeamDay, permittedEmployees]);

    const teamScheduleQueries = useQueries({
        queries: permittedEmployees.map((employee) => ({
            queryKey: ['employeeSchedule', tenantId, employee.id],
            queryFn: () => fetchEmployeeSchedule(employee.id, tenantId),
            enabled: scheduleViewMode === 'team' && Boolean(employee.id),
            staleTime: 1000 * 60 * 20,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        })),
    });

    const teamScheduleByEmployee = useMemo(() => {
        const map = new Map<string, Map<string, string>>();
        permittedEmployees.forEach((employee, index) => {
            const scheduleData = teamScheduleQueries[index]?.data;
            if (scheduleData) {
                map.set(employee.id, scheduleData);
            }
        });
        return map;
    }, [permittedEmployees, teamScheduleQueries]);

    const visibleTeamEmployees = useMemo(() => {
        if (!isMobile) return permittedEmployees;
        return permittedEmployees.filter((employee) => employee.id === selectedEmployeeId);
    }, [isMobile, permittedEmployees, selectedEmployeeId]);

    const getDailyWorkHoursSummary = (
        employeeSchedule: Map<string, string> | null | undefined,
        employeeAppointments: Appointment[] | null | undefined,
        targetDateKey: string,
    ) => {
        if (!employeeSchedule) {
            return { hoursLabel: 'No schedule set', breakLabel: null as string | null };
        }
        const bitString = employeeSchedule.get(targetDateKey);
        if (!bitString) {
            return { hoursLabel: 'No schedule set', breakLabel: null as string | null };
        }
        const info = extractWorkHours(
            (employeeAppointments ?? []).filter(
                (appointment) => appointment.appointmentDate === targetDateKey && !isBlocker(appointment)
            ),
            bitString,
        );
        if (info.isDayOff) {
            return { hoursLabel: 'Day Off', breakLabel: null as string | null };
        }

        const recurringBreaksForDay = (employeeAppointments ?? [])
            .filter(
                (appointment) =>
                    appointment.appointmentDate === targetDateKey &&
                    isBlocker(appointment) &&
                    Boolean(appointment.recurringBreakId)
            )
            .filter((appointment, index, appointments) => {
                const dedupeKey = [
                    appointment.recurringBreakId,
                    appointment.appointmentDate,
                    appointment.appointmentTime,
                    appointment.appointmentDuration,
                ].join('|');

                return index === appointments.findIndex((candidate) => (
                    [
                        candidate.recurringBreakId,
                        candidate.appointmentDate,
                        candidate.appointmentTime,
                        candidate.appointmentDuration,
                    ].join('|') === dedupeKey
                ));
            })
            .sort((a, b) => a.appointmentTime.localeCompare(b.appointmentTime));

        const breakLabel = recurringBreaksForDay.length > 0
            ? recurringBreaksForDay
                .map((appointment) => {
                    const endTime = dayjs(appointment.appointmentTime, 'HHmm')
                        .add(appointment.appointmentDuration, 'minute')
                        .format('h:mm A');
                    return `${dayjs(appointment.appointmentTime, 'HHmm').format('h:mm A')} - ${endTime}`;
                })
                .join(', ')
            : null;

        return {
            hoursLabel: `${info.startTime.format('h:mm A')} - ${info.endTime.format('h:mm A')}`,
            breakLabel,
        };
    };

    const selectedEmployeeAppointments =
        selectedEmployee?.id === currentEmployee?.id
            ? myAppointments
            : (selectedEmployeeAppointmentsQuery.data ?? []);
    const selectedEmployeeSchedule =
        selectedEmployee?.id === currentEmployee?.id
            ? (scheduleQuery.data ?? null)
            : (selectedEmployeeScheduleQuery.data ?? null);
    const selectedEmployeeWorkHoursSummary = selectedEmployee
        ? getDailyWorkHoursSummary(selectedEmployeeSchedule, selectedEmployeeAppointments, dateKey)
        : { hoursLabel: 'No schedule set', breakLabel: null as string | null };
    const hasTopControls =
        (canShowEmployeePicker && isMobile && permittedEmployees.length > 0) ||
        (canShowTeamToggle && !isMobile) ||
        (canShowEmployeePicker && !isMobile && scheduleViewMode === 'perEmployee' && permittedEmployees.length > 0);

    const openManageDayForEmployee = (employeeId: string) => {
        if (selectedEmployeeId !== employeeId) {
            onSelectedEmployeeIdChange(employeeId);
        }
        setManageScheduleOpen(true);
    };
    const goToPreviousDay = () => {
        onSharedDateChange(sharedDate.subtract(1, 'day'));
    };
    const goToNextDay = () => {
        onSharedDateChange(sharedDate.add(1, 'day'));
    };
    const goToToday = () => {
        onSharedDateChange(dayjs.tz());
    };
    const openDatePickerFromElement = (anchorEl: HTMLElement) => {
        setDatePickerAnchorEl(anchorEl);
        setDatePickerOpen(true);
    };

    const hasRenderableAppointmentData =
        appointmentIds.length === 0 ||
        hydrateAppointmentsQuery.isSuccess ||
        myAppointments.length > 0;
    const isInitialAppointmentHydration =
        appointmentIds.length > 0 &&
        hydrateAppointmentsQuery.isLoading &&
        !hasRenderableAppointmentData;

    if (employeeQuery.isLoading || isInitialAppointmentHydration || employeesQuery.isLoading) {
        return <LoadingSpinner label="Loading calendar" />;
    }

    if (employeeQuery.isError || hydrateAppointmentsQuery.isError || employeesQuery.isError || !employeeQuery.data) {
        return (
            <Container disableGutters sx={{ pt: 4 }}>
                <Alert severity="error">
                    Failed to load calendar data. Please refresh the page and try again.
                </Alert>
            </Container>
        );
    }

    const snackbarHost = typeof document === 'undefined'
        ? snackbar
        : <Portal container={document.body}>{snackbar}</Portal>;
    const shouldEmbedEmployeePicker =
        canShowEmployeePicker &&
        scheduleViewMode === 'perEmployee' &&
        permittedEmployees.length > 0;
    const stickyHeaderTop = scheduleViewMode === 'perEmployee'
        ? { xs: 56, sm: 64 }
        : undefined;
    const employeePickerControl = shouldEmbedEmployeePicker ? (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
        >
            {canShowTeamToggle && !isMobile && (
                <ToggleButtonGroup
                    color="secondary"
                    exclusive
                    value={scheduleViewMode}
                    onChange={(_event, next) => {
                        if (!next) return;
                        onScheduleViewModeChange(next);
                    }}
                    size="small"
                >
                    <ToggleButton value="perEmployee">{`Per ${resourceSingular}`}</ToggleButton>
                    <ToggleButton value="team">Side-by-Side</ToggleButton>
                </ToggleButtonGroup>
            )}
            <Box display="flex" justifyContent={{ xs: 'center', sm: 'flex-start' }}>
                <EmployeePicker
                    employees={permittedEmployees}
                    value={selectedEmployeeId}
                    onChange={onSelectedEmployeeIdChange}
                    profilePictures={profilePictures}
                    label={resourceSingular}
                    placeholder={`Select ${resourceSingular.toLowerCase()}`}
                    allOptionLabel={`All ${resourcePlural}`}
                />
            </Box>
        </Stack>
    ) : null;

    return (
        <>
            {snackbarHost}
            {hasTopControls && !shouldEmbedEmployeePicker && (
                <Stack spacing={1.5} sx={{ mb: 1.5 }}>
                    {canShowEmployeePicker && isMobile && permittedEmployees.length > 0 && (
                        <Box display="flex" justifyContent={{ xs: 'center', md: 'flex-start' }} sx={{ mt: -0.5 }}>
                            <EmployeePicker
                                employees={permittedEmployees}
                                value={selectedEmployeeId}
                                onChange={onSelectedEmployeeIdChange}
                                profilePictures={profilePictures}
                                label={resourceSingular}
                                placeholder={`Select ${resourceSingular.toLowerCase()}`}
                                allOptionLabel={`All ${resourcePlural}`}
                            />
                        </Box>
                    )}

                    {canShowTeamToggle && !isMobile && (
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                            <ToggleButtonGroup
                                color="secondary"
                                exclusive
                                value={scheduleViewMode}
                                onChange={(_event, next) => {
                                    if (!next) return;
                                    onScheduleViewModeChange(next);
                                }}
                                size="small"
                            >
                                <ToggleButton value="perEmployee">{`Per ${resourceSingular}`}</ToggleButton>
                                <ToggleButton value="team">Side-by-Side</ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                    )}

                    {canShowEmployeePicker && !isMobile && scheduleViewMode === 'perEmployee' && permittedEmployees.length > 0 && (
                        <Box display="flex" justifyContent={{ xs: 'center', md: 'flex-start' }} sx={{ mt: -0.5 }}>
                            <EmployeePicker
                                employees={permittedEmployees}
                                value={selectedEmployeeId}
                                onChange={onSelectedEmployeeIdChange}
                                profilePictures={profilePictures}
                                label={resourceSingular}
                                placeholder={`Select ${resourceSingular.toLowerCase()}`}
                                allOptionLabel={`All ${resourcePlural}`}
                            />
                        </Box>
                    )}
                </Stack>
            )}

            {scheduleViewMode === 'team' ? (
                <Box>
                    <Box
                        sx={{
                            mb: 1.5,
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            minHeight: 40,
                            flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton color="primary" size="small" onClick={goToPreviousDay}>
                                <ArrowBackIosNew fontSize="small" />
                            </IconButton>
                            <Button
                                variant="text"
                                color="primary"
                                onClick={(event) => openDatePickerFromElement(event.currentTarget)}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: { xs: '1.05rem', sm: '1.3rem', md: '1.45rem', lg: '1.6rem', xl: '1.7rem' },
                                    lineHeight: 1.2,
                                    px: 1,
                                }}
                            >
                                {sharedDate.format('MMMM D, YYYY')}
                            </Button>
                            <IconButton color="primary" size="small" onClick={goToNextDay}>
                                <ArrowForwardIos fontSize="small" />
                            </IconButton>
                        </Box>
                        <Box
                            sx={{
                                position: { xs: 'static', sm: 'absolute' },
                                right: { sm: 0 },
                                display: 'flex',
                                justifyContent: { xs: 'center', sm: 'flex-end' },
                                width: { xs: '100%', sm: 'auto' },
                            }}
                        >
                            <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={goToToday}
                                sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                                Today
                            </Button>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            overflowX: isMobile ? 'visible' : 'auto',
                            flexDirection: isMobile ? 'column' : 'row',
                            pb: 1,
                            '& > *': {
                                flex: isMobile ? '1 1 auto' : '0 0 360px',
                                minWidth: isMobile ? '100%' : 360,
                            },
                        }}
                    >
                        {visibleTeamEmployees.map((employee) => {
                            const teamWorkHoursSummary = getDailyWorkHoursSummary(
                                teamScheduleByEmployee.get(employee.id) ?? null,
                                teamDayAppointmentsByEmployee.get(employee.id) ?? [],
                                dateKey,
                            );
                            return (
                                <Box
                                    key={employee.id}
                                    sx={{
                                        borderRadius: 2,
                                        border: '1px solid rgba(0,0,0,0.12)',
                                        backgroundColor: '#fff',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar
                                                src={profilePictures[employee.id]}
                                                sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: 'grey.300', color: 'text.primary' }}
                                            >
                                                {`${employee.firstName?.[0] ?? ''}${employee.lastName?.[0] ?? ''}`.toUpperCase() || 'E'}
                                            </Avatar>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                {employee.firstName} {employee.lastName}
                                            </Typography>
                                        </Box>
                                        <Box
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => openManageDayForEmployee(employee.id)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    openManageDayForEmployee(employee.id);
                                                }
                                            }}
                                            sx={{
                                                mt: 1,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1.5,
                                                px: 1.25,
                                                py: 0.75,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                cursor: 'pointer',
                                                '&:hover': { backgroundColor: 'action.hover' },
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body2">
                                                    <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>Work Hours:</Box>{' '}
                                                    <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary' }}>{teamWorkHoursSummary.hoursLabel}</Box>
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 0.25 }}>
                                                    <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>Break:</Box>{' '}
                                                    <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary' }}>{teamWorkHoursSummary.breakLabel ?? 'None'}</Box>
                                                </Typography>
                                            </Box>
                                            <EditOutlinedIcon fontSize="small" color="action" />
                                        </Box>
                                    </Box>
                                    <Box sx={{ px: 1, pb: 1 }}>
                                        <CalendarView
                                            employee={employee}
                                            appointments={teamDayAppointmentsByEmployee.get(employee.id) ?? []}
                                            appointmentIds={appointmentIdsByEmployeeForDate.map.get(employee.id) ?? []}
                                            schedule={teamScheduleByEmployee.get(employee.id) ?? null}
                                            sharedDate={sharedDate}
                                            onSharedDateChange={onSharedDateChange}
                                            onlyDayView
                                            hideToolbar
                                            hideCreateButton
                                            embedded
                                            createEmployeeOptions={[employee]}
                                            selectedCreateEmployeeId={employee.id}
                                            onSelectCreateEmployee={() => { }}
                                            internalBookingEmployeesOverride={permittedEmployees}
                                            internalBookingAllowedEmployeeIds={permittedEmployees.map((entry) => entry.id)}
                                            internalBookingPreferredEmployeeId={employee.id}
                                            updateCurrentEmployeeIndexCache={employee.id === currentEmployee?.id}
                                            showSuccess={showSuccess}
                                            showError={showError}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            ) : selectedEmployee ? (
                <CalendarView
                    employee={selectedEmployee}
                    appointments={selectedEmployeeAppointments}
                    appointmentIds={
                        selectedEmployee.id === currentEmployee?.id
                            ? appointmentIds
                            : selectedEmployeeAllAppointmentIds
                    }
                    schedule={selectedEmployeeSchedule}
                    sharedDate={sharedDate}
                    onSharedDateChange={onSharedDateChange}
                    showSuccess={showSuccess}
                    showError={showError}
                    createEmployeeOptions={[selectedEmployee]}
                    selectedCreateEmployeeId={selectedEmployee.id}
                    onSelectCreateEmployee={() => { }}
                    internalBookingEmployeesOverride={permittedEmployees}
                    internalBookingAllowedEmployeeIds={permittedEmployees.map((employee) => employee.id)}
                    internalBookingPreferredEmployeeId={selectedEmployee.id}
                    updateCurrentEmployeeIndexCache={selectedEmployee.id === currentEmployee?.id}
                    onCalendarTitleClick={openDatePickerFromElement}
                    workHoursLabel={selectedEmployeeWorkHoursSummary.hoursLabel}
                    workHoursBreakLabel={selectedEmployeeWorkHoursSummary.breakLabel}
                    onWorkHoursClick={() => openManageDayForEmployee(selectedEmployee.id)}
                    stickyHeaderTop={stickyHeaderTop}
                    stickyHeaderControls={employeePickerControl}
                />
            ) : (
                <Alert severity="info">{`No ${resourcePlural.toLowerCase()} found.`}</Alert>
            )}
            <DatePicker
                open={datePickerOpen}
                onClose={() => setDatePickerOpen(false)}
                value={sharedDate}
                onChange={(next) => {
                    if (next) {
                        onSharedDateChange(next);
                    }
                    setDatePickerOpen(false);
                }}
                slotProps={{
                    textField: { sx: { display: 'none' } },
                    popper: {
                        anchorEl: datePickerAnchorEl,
                        placement: 'bottom-start',
                    },
                }}
            />
            <Dialog
                open={manageScheduleOpen}
                onClose={() => setManageScheduleOpen(false)}
                fullWidth
                maxWidth="sm"
                TransitionComponent={ManageScheduleTransition}
                transitionDuration={{ enter: 280, exit: 220 }}
                sx={{
                    '& .MuiDialog-paper': {
                        width: { xs: '100%', sm: 560 },
                        maxWidth: { xs: '100%', sm: 560 },
                        m: { xs: 0, sm: 2 },
                        borderRadius: { xs: '20px 20px 0 0', sm: 2 },
                        maxHeight: { xs: '88dvh', sm: 'calc(100% - 64px)' },
                        alignSelf: { xs: 'flex-end', sm: 'center' },
                    },
                }}
            >
                <DialogTitle sx={{ pr: 6 }}>
                    Manage Schedule for {sharedDate.format('MMM D, YYYY')}
                    <IconButton color="secondary" onClick={() => setManageScheduleOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ px: { xs: 1.5, sm: 2 }, pb: 2 }}>
                    {!selectedEmployee ? (
                        <Alert severity="info">Pick an employee to manage schedule for this date.</Alert>
                    ) : (
                        <EmployeeSchedulePanel
                            employee={selectedEmployee}
                            selectedDate={sharedDate}
                            showAppointments={false}
                            alwaysOpenScheduleEditor
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default CalendarScreen;
